import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, SpeakerWaveIcon, MusicalNoteIcon, PlayIcon, PauseIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { IAudioGeneration, IUserModel, TaskStatus } from '@packages/global';
import { audioGenerationApi } from '../../services/user/audioGeneration.api';
import { useCoinStore } from '../../stores/coinStore';
import AudioUpload from '../common/AudioUpload';
import { EmotionSelector, AlphaSlider, SpeedSlider, ModelSelector } from '../common/form';

interface AudioGenerationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (audioGeneration: IAudioGeneration) => void;
  models: IUserModel[];
  modelsLoading: boolean;
  initialPrompt?: string;
  initialSpkAudioUrl?: string;
}

interface AudioGenerationFormData {
  prompt: string;
  spkAudioUrl: string;
  emoAudioUrl?: string;
  emoAlpha?: number;
  emoText?: string;
  speedFactor?: number;
  matchReferenceDuration?: boolean;
  modelId: string;
}

export default function AudioGenerationDialog({
  isOpen,
  onClose,
  onConfirm,
  models,
  modelsLoading,
  initialPrompt = '',
  initialSpkAudioUrl = ''
}: AudioGenerationDialogProps) {
  const { t } = useTranslation(['generation', 'common']);
  const [formData, setFormData] = useState<AudioGenerationFormData>({
    prompt: initialPrompt,
    spkAudioUrl: initialSpkAudioUrl,
    emoAudioUrl: '',
    emoAlpha: 0.5,
    emoText: '',
    speedFactor: 1.72,
    matchReferenceDuration: false,
    modelId: ''
  });
  const [currentTask, setCurrentTask] = useState<IAudioGeneration | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { account } = useCoinStore();

  const selectedModel = models.find(m => m.id === formData.modelId);
  const modelPrice = selectedModel?.coinPrice ?? 0;

  // 按字符数预估时长和费用（约10字符/秒）
  const CHARS_PER_SECOND = 10;
  const estimatedSeconds = formData.prompt.length > 0
    ? Math.ceil(formData.prompt.length / CHARS_PER_SECOND / (formData.speedFactor || 1))
    : 0;
  const estimatedCost = modelPrice * estimatedSeconds;
  const hasEnoughCoins = (account?.balance ?? 0) >= estimatedCost;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);

  // Auto-select first model when models load
  useEffect(() => {
    if (!modelsLoading && !formData.modelId && models.length > 0) {
      setFormData(prev => ({ ...prev, modelId: models[0].id }));
    }
  }, [models, modelsLoading, formData.modelId]);

  // Reset form when dialog opens with new initial values
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        prompt: initialPrompt,
        spkAudioUrl: initialSpkAudioUrl
      }));
    }
  }, [isOpen, initialPrompt, initialSpkAudioUrl]);

  // Poll for task status
  useEffect(() => {
    if (!currentTask) return;
    if (currentTask.status === TaskStatus.COMPLETED || currentTask.status === TaskStatus.FAILED) {
      setIsGenerating(false);
      return;
    }

    const interval = setInterval(async () => {
      try {
        const updated = await audioGenerationApi.getAudioGeneration(currentTask.id);
        setCurrentTask(updated);
        if (updated.status === TaskStatus.COMPLETED || updated.status === TaskStatus.FAILED) {
          setIsGenerating(false);
        }
      } catch (error) {
        console.error('轮询失败:', error);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [currentTask?.id, currentTask?.status]);

  // Reset audio playback when result changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = currentTask?.resultAudioUrl || '';
    }
    setAudioPlaying(false);
  }, [currentTask?.resultAudioUrl]);

  const handleFormDataChange = (data: Partial<AudioGenerationFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const toggleAudioPlay = () => {
    if (!currentTask?.resultAudioUrl) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(currentTask.resultAudioUrl);
      audioRef.current.onended = () => setAudioPlaying(false);
    }
    if (audioPlaying) {
      audioRef.current.pause();
      setAudioPlaying(false);
    } else {
      audioRef.current.play();
      setAudioPlaying(true);
    }
  };

  const handleGenerate = async () => {
    if (!formData.modelId || !formData.prompt.trim() || !formData.spkAudioUrl) return;

    try {
      setIsGenerating(true);
      const task = await audioGenerationApi.createAudioGeneration({
        prompt: formData.prompt,
        spkAudioUrl: formData.spkAudioUrl,
        emoAudioUrl: formData.emoAudioUrl || undefined,
        emoAlpha: formData.emoAlpha,
        emoText: formData.emoText || undefined,
        speedFactor: formData.speedFactor,
        matchReferenceDuration: formData.matchReferenceDuration,
        modelId: formData.modelId
      });
      setCurrentTask(task);
    } catch (error) {
      console.error('生成失败:', error);
      setIsGenerating(false);
    }
  };

  const handleConfirm = () => {
    if (currentTask && currentTask.status === TaskStatus.COMPLETED) {
      onConfirm(currentTask);
      handleClose();
    }
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setCurrentTask(null);
    setIsGenerating(false);
    setAudioPlaying(false);
    setFormData(prev => ({
      ...prev,
      prompt: '',
      spkAudioUrl: '',
      emoAudioUrl: '',
      emoText: '',
      matchReferenceDuration: false
    }));
    onClose();
  };

  const hasEmotion = formData.emoText || formData.emoAudioUrl;

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full rounded-xl bg-bg-surface shadow-2xl border border-border-subtle">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border-subtle">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-green-600/20 rounded-lg flex items-center justify-center">
                <SpeakerWaveIcon className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <Dialog.Title className="text-base font-semibold text-gray-50">
                  {t('audio.title')}
                </Dialog.Title>
                <p className="text-xs text-gray-400 mt-0.5">{t('audio.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-50 transition-colors p-1.5 hover:bg-bg-elevated rounded-lg cursor-pointer"
              disabled={isGenerating}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content - Single Column Layout */}
          <div className="p-4 space-y-4">
            {/* 模型选择 */}
            <ModelSelector
              models={models}
              selectedModelId={formData.modelId}
              onChange={(modelId) => handleFormDataChange({ modelId })}
              loading={modelsLoading}
              placeholder={t('audio.form.selectModel')}
            />

            {/* 文本内容输入 */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                {t('audio.form.prompt')}
              </label>
              <textarea
                value={formData.prompt}
                onChange={(e) => handleFormDataChange({ prompt: e.target.value })}
                rows={3}
                disabled={isGenerating}
                className="w-full border border-border-subtle rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-info bg-bg-form text-gray-50 text-sm disabled:opacity-50 resize-none"
                placeholder={t('audio.form.promptPlaceholder')}
              />
            </div>

            {/* 参考音频上传 */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                {t('audio.form.referenceAudio')}
              </label>
              {formData.spkAudioUrl ? (
                <div className="bg-bg-elevated rounded-lg p-2.5 border border-border-subtle flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <SpeakerWaveIcon className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <span className="text-xs text-gray-300 truncate">
                      {t('audio.form.audioUploaded')}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFormDataChange({ spkAudioUrl: '', matchReferenceDuration: false })}
                    disabled={isGenerating}
                    className="p-1 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50 cursor-pointer flex-shrink-0"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <AudioUpload
                  value=""
                  onUploadSuccess={(url) => handleFormDataChange({ spkAudioUrl: url })}
                  placeholder={t('audio.form.uploadSpkAudio')}
                  showToneSelector={true}
                />
              )}
            </div>

            {/* 参数设置工具栏 - 水平排列，与 AudioGenerationForm 一致 */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* 情感选择器 */}
              {!formData.matchReferenceDuration && (
                <EmotionSelector
                  emoText={formData.emoText || ''}
                  emoAudioUrl={formData.emoAudioUrl}
                  onEmoTextChange={(text) => handleFormDataChange({ emoText: text })}
                  onEmoAudioChange={(url) => handleFormDataChange({ emoAudioUrl: url, emoText: url ? '' : formData.emoText })}
                  showAudioUpload={true}
                />
              )}

              {/* 情感强度滑块 */}
              {hasEmotion && !formData.matchReferenceDuration && (
                <AlphaSlider
                  value={formData.emoAlpha || 0.5}
                  onChange={(value) => handleFormDataChange({ emoAlpha: value })}
                />
              )}

              {/* 语速控制 */}
              {!formData.matchReferenceDuration && (
                <SpeedSlider
                  value={formData.speedFactor || 1.72}
                  onChange={(value) => handleFormDataChange({ speedFactor: value })}
                />
              )}

              {/* 匹配时长开关 */}
              <button
                type="button"
                onClick={() => formData.spkAudioUrl && handleFormDataChange({ matchReferenceDuration: !formData.matchReferenceDuration })}
                disabled={!formData.spkAudioUrl}
                className={`p-1.5 rounded-lg transition-colors ${!formData.spkAudioUrl
                  ? 'bg-bg-elevated text-gray-600 cursor-not-allowed border border-border-default'
                  : formData.matchReferenceDuration
                    ? 'bg-blue-600 text-white'
                    : 'bg-bg-elevated text-gray-400 hover:bg-border-default hover:text-gray-300 border border-border-default'
                  }`}
                title={!formData.spkAudioUrl ? t('audio.form.matchDurationTooltipDisabled') : t('audio.form.matchDurationTooltip')}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 8v4l3 3" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="12" r="9" />
                </svg>
              </button>
            </div>

            {/* 生成按钮与费用信息 */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !formData.prompt.trim() || !formData.spkAudioUrl || !hasEnoughCoins || !formData.modelId}
                className="flex-1 bg-gradient-to-br from-green-600 to-green-700 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2.5 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-green-500/50 cursor-pointer text-sm"
              >
                {isGenerating ? t('common:generating') : t('audio.form.generateAudio')}
              </button>

              {/* 费用显示 */}
              {modelPrice > 0 && estimatedSeconds > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <CurrencyDollarIcon className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-gray-400">
                    <span className="font-mono text-primary">{estimatedCost}</span>
                    <span className="text-gray-500 ml-1">({estimatedSeconds}{t('audio.form.seconds')})</span>
                  </span>
                  <span className="text-gray-600">|</span>
                  <span className="text-gray-400">
                    {t('audio.form.balance')}:
                    <span className={`font-mono ml-1 ${hasEnoughCoins ? 'text-gray-300' : 'text-error'}`}>
                      {account?.balance ?? 0}
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* 余额不足提示 */}
            {!hasEnoughCoins && modelPrice > 0 && estimatedSeconds > 0 && (
              <p className="text-xs text-error">
                {t('audio.form.insufficientCoins', { balance: account?.balance ?? 0, price: estimatedCost })}
              </p>
            )}

            {/* Preview Area - 水平条状 */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-2">{t('common:preview')}</label>
              <div className="bg-bg-elevated rounded-lg border border-border-subtle p-4">
                {currentTask?.resultAudioUrl ? (
                  <div className="flex items-center gap-4">
                    <button
                      onClick={toggleAudioPlay}
                      className="w-10 h-10 rounded-full bg-green-600 hover:bg-green-500 flex items-center justify-center transition-colors flex-shrink-0"
                    >
                      {audioPlaying ? (
                        <PauseIcon className="w-5 h-5 text-white" />
                      ) : (
                        <PlayIcon className="w-5 h-5 text-white ml-0.5" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <audio
                        ref={audioRef}
                        src={currentTask.resultAudioUrl}
                        className="hidden"
                        onEnded={() => setAudioPlaying(false)}
                      />
                      <div className="flex items-center gap-2 text-sm text-gray-300">
                        <MusicalNoteIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="truncate">{t('audio.form.generationComplete')}</span>
                      </div>
                    </div>
                    {currentTask.status === TaskStatus.FAILED && (
                      <div className="text-xs text-error flex-shrink-0">
                        {t('audio.form.generationFailed', { message: currentTask.resultMessage || t('audio.form.unknownError') })}
                      </div>
                    )}
                  </div>
                ) : isGenerating ? (
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 flex-shrink-0"></div>
                    <span className="text-sm text-gray-400">
                      {currentTask?.status === TaskStatus.PENDING || currentTask?.status === TaskStatus.SUBMITTED
                        ? t('audio.form.waitingGeneration')
                        : t('common:generating')}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-gray-500">
                    <SpeakerWaveIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">{t('audio.form.noAudio')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-4 border-t border-border-subtle">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 text-gray-300 bg-bg-elevated rounded-lg hover:bg-bg-surface transition-colors font-medium cursor-pointer text-sm"
              disabled={isGenerating}
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!currentTask || currentTask.status !== TaskStatus.COMPLETED}
              className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium cursor-pointer text-sm"
            >
              {t('common:confirm')}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}